import { BooleanQueryError, Node, Token } from './types';

const flatten = (type: 'and' | 'or', children: Node[]): Node => {
  const merged = children.reduce<Node[]>((acc, child) => {
    if (child.type === type) {
      return [...acc, ...child.children];
    }
    return [...acc, child];
  }, []);

  if (merged.length === 1) {
    return merged[0];
  }

  return { type, children: merged };
};

class Parser {
  private readonly tokens: Token[];

  private position = 0;

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  parse(): Node {
    if (this.tokens.length === 0) {
      throw new BooleanQueryError('empty_group');
    }

    const node = this.parseOr();

    if (this.current()) {
      throw new BooleanQueryError('unbalanced_parenthesis');
    }

    return node;
  }

  private current(): Token | undefined {
    return this.tokens[this.position];
  }

  private advance(): Token {
    const token = this.tokens[this.position];
    this.position += 1;
    return token;
  }

  private parseOr(): Node {
    const children = [this.parseAnd()];

    while (this.current()?.type === 'or') {
      this.advance();
      if (!this.canStartUnary()) {
        throw new BooleanQueryError('dangling_operator');
      }
      children.push(this.parseAnd());
    }

    return flatten('or', children);
  }

  private parseAnd(): Node {
    const children = [this.parseUnary()];

    while (this.canStartUnary() || this.current()?.type === 'and') {
      if (this.current()?.type === 'and') {
        this.advance();
        if (!this.canStartUnary()) {
          throw new BooleanQueryError('dangling_operator');
        }
      }
      children.push(this.parseUnary());
    }

    return flatten('and', children);
  }

  private parseUnary(): Node {
    if (this.current()?.type === 'not') {
      this.advance();
      if (!this.canStartUnary()) {
        throw new BooleanQueryError('dangling_operator');
      }
      const child = this.parseUnary();
      if (child.type === 'not') {
        return child.child;
      }
      return { type: 'not', child };
    }

    return this.parsePrimary();
  }

  private parsePrimary(): Node {
    const token = this.current();

    if (!token) {
      throw new BooleanQueryError('dangling_operator');
    }

    if (token.type === 'term') {
      this.advance();
      return { type: 'term', value: token.value };
    }

    if (token.type === 'lparen') {
      this.advance();

      if (this.current()?.type === 'rparen') {
        throw new BooleanQueryError('empty_group');
      }

      if (!this.current()) {
        throw new BooleanQueryError('unbalanced_parenthesis');
      }

      const node = this.parseOr();

      if (this.current()?.type !== 'rparen') {
        throw new BooleanQueryError('unbalanced_parenthesis');
      }
      this.advance();

      return node;
    }

    if (token.type === 'rparen') {
      throw new BooleanQueryError('unbalanced_parenthesis');
    }

    throw new BooleanQueryError('dangling_operator');
  }

  private canStartUnary(): boolean {
    const token = this.current();
    return (
      token?.type === 'term' ||
      token?.type === 'lparen' ||
      token?.type === 'not'
    );
  }
}

export const parse = (tokens: Token[]): Node => new Parser(tokens).parse();
